import React from 'react';
import { ArrowRight, Factory, Building2, Target, Shield, Cog, Globe } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import type { PageType } from './Header';

type AboutSectionProps = {
  onNavigate: (page: PageType) => void;
};

const stats = [
  { value: '45+', label: 'Years Experience', icon: Target },
  { value: '5,000+ sq.m', label: 'Manufacturing Area', icon: Factory },
  { value: '250 Tonnes', label: 'Handling Capacity', icon: Cog },
  { value: '7 Independent Bays', label: 'Dedicated Machining and Fabrication', icon: Building2 },
  { value: '5+ Countries', label: 'Exported Globally', icon: Globe },
  { value: 'ISO Certified', label: 'Complex Machining and CNC Capabilities', icon: Shield },
];

export const AboutSection: React.FC<AboutSectionProps> = ({ onNavigate }) => {
  const { isDark } = useTheme();

  return (
    <section className={`w-full pt-8 md:pt-10 pb-20 md:pb-28 relative overflow-hidden transition-colors duration-300 ${
      isDark ? 'bg-gradient-to-br from-vmil-navy via-vmil-navy to-vmil-blue/30' : 'bg-gradient-to-br from-gray-50 via-white to-vmil-blue/5'
    }`}>
      {/* Decorative Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className={`absolute -top-40 -right-40 w-96 h-96 rounded-full blur-3xl ${isDark ? 'bg-vmil-blue/10' : 'bg-vmil-blue/10'}`} />
        <div className={`absolute -bottom-40 -left-40 w-96 h-96 rounded-full blur-3xl ${isDark ? 'bg-vmil-blue/20' : 'bg-vmil-blue/10'}`} />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-10 items-start pt-4 sm:pt-6 md:pt-8">
          {/* Left - Founder's Statue */}
          <div className="order-2 lg:order-1 pt-4 sm:pt-6 md:pt-8">
            <div className={`relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl ${
              isDark ? 'border border-white/10' : 'border border-gray-200'
            }`}>
              <img 
                src="/statue.png" 
                alt="Founder's Statue"
                className="w-full h-[300px] sm:h-[350px] md:h-[400px] lg:h-[450px] object-cover"
              />
            </div>
          </div>

          {/* Right - Text Content */}
          <div className="order-1 lg:order-2 pt-4 sm:pt-6 md:pt-8">
            {/* Section Badge */}
            <div className={`inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 rounded-full border mb-2 sm:mb-3 ${
              isDark 
                ? 'bg-vmil-blue/10 border-vmil-blue/20' 
                : 'bg-vmil-blue/10 border-vmil-blue/20'
            }`}>
              <span className={`text-xs sm:text-sm font-semibold uppercase tracking-wide ${isDark ? 'text-vmil-blue' : 'text-vmil-blue'}`}>
                About Velmurugan Industries
              </span>
            </div>

            <h2 className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold mb-2 sm:mb-3 leading-tight ${
              isDark ? 'text-white' : 'text-vmil-blue'
            }`}>
              Our Engineering Journey
              <span className={`block ${isDark ? 'text-vmil-blue' : 'text-vmil-blue'}`}>Since 1979</span>
            </h2>

            <div className="space-y-6 sm:space-y-8 mb-6 sm:mb-8">
              <p className={`text-sm sm:text-base lg:text-lg leading-relaxed mt-8 ${isDark ? 'text-white/90' : 'text-vmil-steel'}`}>
                Established in <span className={`font-semibold ${isDark ? 'text-vmil-blue' : 'text-vmil-blue'}`}>1979</span>, <strong className={isDark ? 'text-white' : 'text-vmil-blue'}>Velmurugan Industries Pvt. Ltd. (VMIL)</strong> is an <span className={`font-semibold ${isDark ? 'text-vmil-blue' : 'text-vmil-blue'}`}>engineering-led manufacturing partner</span> serving global core industrial sectors. We work closely with our customers, from design through to disciplined execution, structured processes, and dependable delivery.
              </p>

              {/* Capabilities - prose */}
              <div className={`!mt-4 ${isDark ? '' : ''}`}>
                <p className={`text-sm sm:text-base lg:text-lg leading-relaxed ${isDark ? 'text-white/85' : 'text-vmil-steel'}`}>
                  Our capabilities span heavy and precision equipment manufacturing for cement and steel plants, mineral handling systems, wind turbine components, earth-moving equipment, plastic processing machinery,<br />boiler components, oil coolers, and other critical industrial applications.
                </p>
              </div>

              {/* Philosophy */}
              <div className={`pt-4 border-t !mt-4 ${isDark ? 'border-white/10' : 'border-vmil-blue/10'}`}>
                <p className={`text-sm sm:text-base lg:text-lg leading-relaxed ${isDark ? 'text-white/85' : 'text-vmil-steel'}`}>
                  A strong QHSE mindset, hands-on management, collaborative teams, and open communication, supported by ISO 9001 • 14001 • 45001 • 3834-2 to deliver reliable outcomes across diverse and demanding projects.
                </p>
              </div>

              {/* Focus & Quote */}
              <div className={`pt-4 border-t !mt-4 ${isDark ? 'border-white/10' : 'border-vmil-blue/10'}`}>
                <blockquote className={`pl-4 sm:pl-5 border-l-4 border-vmil-blue italic text-vmil-blue text-sm sm:text-base lg:text-lg leading-relaxed`}>
                  <p className="mb-3">
                    Our focus has always been on doing the fundamentals right: engineering discipline, quality execution, and long-term relationships.
                  </p>
                  <cite className="font-semibold block">
                    Mr. Sampath, Managing Director & CEO
                  </cite>
                </blockquote>
              </div>
            </div>

            {/* CTA Button */}
            <button
              onClick={() => onNavigate('about')}
              className="group inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 rounded-xl bg-vmil-blue text-white font-bold text-sm sm:text-base shadow-lg shadow-vmil-blue/25 transition-all hover:shadow-xl hover:shadow-vmil-blue/30 hover:scale-[1.02]"
            >
              Discover Our Full Story
              <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </div>

        {/* Stats Bar - Below About section */}
        <div className="mt-16 sm:mt-20 md:mt-24 w-full grid grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3 md:gap-4">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            const isLongValue = stat.value.length > 12;
            return (
              <div
                key={index}
                className={`group relative flex flex-col items-center justify-center p-3 sm:p-4 md:p-5 rounded-lg sm:rounded-xl border transition-all duration-300 hover:shadow-lg ${
                  isDark 
                    ? 'bg-vmil-blue/20 border-vmil-blue/30 hover:bg-vmil-blue/30' 
                    : 'bg-white border-gray-200/80 hover:border-vmil-blue/20 shadow-sm hover:shadow-xl'
                }`}
              >
                <div className={`flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-md mb-2 sm:mb-2.5 ${
                  isDark ? 'bg-vmil-blue/30' : 'bg-vmil-blue/10'
                }`}>
                  <Icon className={`w-4 h-4 sm:w-4 sm:h-4 ${isDark ? 'text-vmil-blue' : 'text-vmil-blue'}`} />
                </div>
                <div className={`text-center font-extrabold mb-0.5 leading-tight ${
                  isLongValue 
                    ? 'text-lg sm:text-xl md:text-2xl' 
                    : 'text-xl sm:text-2xl md:text-3xl'
                } ${isDark ? 'text-white' : 'text-vmil-blue'}`}>
                  {stat.value}
                </div>
                <div className={`text-xs sm:text-sm font-medium text-center leading-snug min-h-[1.75rem] ${
                  isDark ? 'text-white/75' : 'text-vmil-steel'
                }`}>
                  {stat.label}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
